<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { CreditCard, Plus, RefreshCw, Trash2 } from 'lucide-vue-next'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import type { EmployeeCard } from '../types/hr'

const props = defineProps<{
  /** Existing employee. Omit (or pass `null`) while creating: cards are queued locally. */
  employeeId?: number | null
  /** Rendered inside another card (create form) instead of as its own panel. */
  embedded?: boolean
}>()

/** Card UIDs queued while the employee has not been created yet. */
const pendingUids = defineModel<string[]>('pending', { default: () => [] })

const { t } = useI18n()
const auth = useAuthStore()
const { listEmployeeCards, createEmployeeCard, updateEmployeeCard, deleteEmployeeCard } = useHrApi()

/** Rows as rendered by the table: stored cards, or the pending queue. */
interface CardRow {
  key: string
  id: number | null
  card_uid: string
  issued_at: string | null
  is_active: number
  revoked_at: string | null
}

const cards = ref<EmployeeCard[]>([])
const cardUid = ref('')
/** Nothing to fetch while the employee does not exist yet (create form). */
const loading = ref(props.employeeId !== undefined && props.employeeId !== null)
const saving = ref(false)
const error = ref('')

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('kiosk.manage'))

/** No employee yet (create form): collect UIDs instead of calling the API. */
const deferred = computed(() => props.employeeId === undefined || props.employeeId === null)

const rows = computed<CardRow[]>(() =>
  deferred.value
    ? pendingUids.value.map((uid) => ({
        key: `pending-${uid}`,
        id: null,
        card_uid: uid,
        issued_at: null,
        is_active: 1,
        revoked_at: null,
      }))
    : cards.value.map((card) => ({
        key: `card-${card.id}`,
        id: card.id,
        card_uid: card.card_uid,
        issued_at: card.issued_at,
        is_active: card.is_active,
        revoked_at: card.revoked_at,
      })),
)

function normalizeUid(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase()
}

function apiDateTime(date = new Date()): string {
  return date.toISOString().slice(0, 19).replace('T', ' ')
}

async function load() {
  if (deferred.value) {
    loading.value = false
    return
  }
  loading.value = true
  error.value = ''
  try {
    const result = await listEmployeeCards({ employee_id: props.employeeId ?? undefined, page: 1, per_page: 100 })
    cards.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function addCard() {
  const uid = normalizeUid(cardUid.value)
  if (!uid) {
    error.value = t('hr.employeeCards.uidRequired')
    return
  }

  error.value = ''

  if (deferred.value) {
    if (pendingUids.value.some((existing) => existing === uid)) {
      error.value = t('hr.employeeCards.duplicateUid')
      return
    }
    pendingUids.value = [...pendingUids.value, uid]
    cardUid.value = ''
    return
  }

  saving.value = true
  try {
    await createEmployeeCard({
      employee_id: props.employeeId as number,
      card_uid: uid,
      is_active: 1,
      issued_at: apiDateTime(),
      revoked_at: null,
    })
    cardUid.value = ''
    await load()
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

async function setActive(card: CardRow, active: boolean) {
  if (card.id === null) return
  saving.value = true
  error.value = ''
  try {
    await updateEmployeeCard(card.id, {
      is_active: active ? 1 : 0,
      revoked_at: active ? null : apiDateTime(),
    })
    await load()
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

async function remove(card: CardRow) {
  if (card.id === null) {
    pendingUids.value = pendingUids.value.filter((uid) => uid !== card.card_uid)
    return
  }
  if (!window.confirm(t('hr.employeeCards.deleteConfirm', { uid: card.card_uid }))) return
  saving.value = true
  error.value = ''
  try {
    await deleteEmployeeCard(card.id)
    await load()
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <section :class="['employee-cards', { 'employee-cards--embedded': embedded }]">
    <header class="employee-cards__header">
      <div>
        <h3><CreditCard :size="18" aria-hidden="true" /> {{ t('hr.employeeCards.title') }}</h3>
        <p>{{ deferred ? t('hr.employeeCards.pendingHint') : t('hr.employeeCards.description') }}</p>
      </div>
      <button
        v-if="!deferred"
        class="btn-secondary icon-button"
        type="button"
        :disabled="loading"
        @click="load"
      >
        <RefreshCw :size="16" aria-hidden="true" />
        {{ t('hr.common.retry') }}
      </button>
    </header>

    <div v-if="canManage" class="employee-cards__create">
      <label for="employee-card-uid">{{ t('hr.employeeCards.uid') }}</label>
      <div class="employee-cards__create-row">
        <input
          id="employee-card-uid"
          v-model="cardUid"
          type="text"
          autocomplete="off"
          :placeholder="t('hr.employeeCards.uidPlaceholder')"
          :disabled="saving"
          @keydown.enter.prevent="addCard"
        >
        <button class="btn-primary" type="button" :disabled="saving || !cardUid.trim()" @click="addCard">
          <Plus :size="16" aria-hidden="true" />
          {{ t('hr.employeeCards.add') }}
        </button>
      </div>
      <small>{{ t('hr.employeeCards.scanHint') }}</small>
    </div>

    <p v-if="error" class="error-inline">{{ error }}</p>
    <p v-if="loading" class="empty-inline">{{ t('hr.employeeCards.loading') }}</p>
    <p v-else-if="rows.length === 0" class="empty-inline">{{ t('hr.employeeCards.empty') }}</p>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.employeeCards.uid') }}</th>
            <th>{{ t('hr.employeeCards.issuedAt') }}</th>
            <th>{{ t('hr.common.status') }}</th>
            <th v-if="canManage">{{ t('hr.employeeCards.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="card in rows" :key="card.key">
            <td class="cell-code">{{ card.card_uid }}</td>
            <td>
              {{ card.issued_at ? formatDisplayDate(card.issued_at) : deferred ? t('hr.employeeCards.pendingStatus') : '—' }}
            </td>
            <td>
              <span
                v-if="card.id === null"
                class="status-badge status-info"
              >
                {{ t('hr.employeeCards.pendingStatus') }}
              </span>
              <template v-else>
                <span :class="['status-badge', card.is_active === 1 ? 'status-ok' : 'status-neutral']">
                  {{ card.is_active === 1 ? t('hr.common.active') : t('hr.common.inactive') }}
                </span>
                <small v-if="card.revoked_at" class="cell-sub">
                  {{ t('hr.employeeCards.revokedAt') }}: {{ formatDisplayDate(card.revoked_at) }}
                </small>
              </template>
            </td>
            <td v-if="canManage" class="employee-cards__actions">
              <button
                v-if="card.id !== null"
                class="link-action"
                type="button"
                :disabled="saving"
                @click="setActive(card, card.is_active !== 1)"
              >
                {{ card.is_active === 1 ? t('hr.employeeCards.deactivate') : t('hr.employeeCards.activate') }}
              </button>
              <button class="link-action danger" type="button" :disabled="saving" @click="remove(card)">
                <Trash2 :size="14" aria-hidden="true" />
                {{ t('hr.common.delete') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped>
.employee-cards { display: grid; gap: 16px; margin-top: 20px; padding: 20px; border: 1px solid #dfe5e2; background: #fff; }
.employee-cards--embedded { margin-top: 0; padding: 0; border: 0; background: transparent; }
.employee-cards__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.employee-cards__header h3 { display: flex; align-items: center; gap: 8px; margin: 0; }
.employee-cards__header p { margin: 5px 0 0; color: #66726e; }
.employee-cards__create { display: grid; gap: 6px; max-width: 680px; }
.employee-cards__create-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px; }
.employee-cards__create-row input { min-height: 42px; padding: 8px 10px; border: 1px solid #b9c5c1; }
.employee-cards__create-row button, .icon-button { display: inline-flex; align-items: center; justify-content: center; gap: 7px; }
.employee-cards__actions { display: flex; align-items: center; gap: 12px; }
.employee-cards__actions button { display: inline-flex; align-items: center; gap: 5px; }
@media (max-width: 640px) {
  .employee-cards__header { align-items: stretch; flex-direction: column; }
  .employee-cards__create-row { grid-template-columns: 1fr; }
  .employee-cards__actions { align-items: flex-start; flex-direction: column; }
}
</style>
